import * as ReactHelmetAsync from 'react-helmet-async';
const Helmet = ReactHelmetAsync.Helmet || (ReactHelmetAsync as any).default?.Helmet;
import { ExternalLink } from 'lucide-react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { PageHero } from '@/components/sections/PageHero';
import { Gallery } from '@/components/ui/Gallery';
import { useHomeData } from '@/hooks/useHomeData';

export function Resources() {
  const { homeData } = useHomeData();

  return (
    <>
      <Helmet>
        <title>Training Resources & Documents | Total Tailwheel</title>
        <meta
          name="description"
          content="Access important training resources including aircraft rental agreements, Cessna 170 owner's manual, and other essential documents for tailwheel training."
        />
        <meta name="keywords" content="tailwheel training resources, aircraft rental agreement, Cessna 170 manual, flight training documents Massachusetts" />
        <meta property="og:title" content="Training Resources & Documents | Total Tailwheel" />
        <meta property="og:description" content="Essential training resources and documents for our tailwheel flight training program." />
        <link rel="canonical" href="https://totaltailwheel.com/resources" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Navigation />
        
        <PageHero title="Resources" />

        {/* Resources Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-medium text-primary text-center mb-8">
                {homeData.resources.title}
              </h2>
              <div className="space-y-8">
                {homeData.resources.sections.map((section, index) => (
                  <div key={index} className="bg-white p-8 rounded-lg shadow-sm border border-secondary/20">
                    <h3 className="text-xl font-medium text-primary mb-4">{section.title}</h3>
                    <p className="text-foreground/80 mb-6">{section.description}</p>
                    
                    {/* Render links if available */}
                    {section.links && section.links.length > 0 && (
                      <div className="space-y-3">
                        {section.links.map((link, linkIndex) => (
                          <a
                            key={linkIndex}
                            href={link.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                          >
                            <ExternalLink className="text-primary mr-3 flex-shrink-0" size={20} />
                            <span className="text-foreground group-hover:text-primary transition-colors font-medium">
                              {link.text}
                            </span>
                          </a>
                        ))}
                      </div>
                    )}
                    
                    {/* Render gallery if available */}
                    {section.gallery && section.gallery.length > 0 && (
                      <div className="mt-6">
                        <Gallery images={section.gallery} columns={3} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Additional Resources */}
              <div className="mt-12 bg-white p-8 rounded-lg shadow-sm border border-secondary/20">
                <h3 className="text-xl font-medium text-primary mb-4">Additional Training Information</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Before Your First Flight</h4>
                    <p className="text-blue-800 text-sm">
                      Please review the aircraft rental agreement and schedules before your first training session. 
                      These documents outline important safety procedures and rental terms.
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Aircraft Documentation</h4>
                    <p className="text-green-800 text-sm">
                      The official Cessna 170 Owner's Manual is always available in the aircraft for reference during training. 
                      The digital copy provided here is for pre-flight study and reference.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}